export const problems = [{
  icon: 'TrendingDown',
  stat: '94%',
  text: 'of agents lose leads to slow, inconsistent follow-up — the deal goes to whoever replies first.'
}, {
  icon: 'Clock',
  stat: '20+ hrs',
  text: 'burned every week on repetitive admin: listing copy, CMAs, emails, scheduling, data entry.'
}, {
  icon: 'BanknoteX',
  stat: '6-figures',
  text: 'in commissions quietly handed to tech-savvy competitors who answer, qualify and nurture 24/7.'
}, {
  icon: 'BatteryLow',
  stat: 'Burnout',
  text: 'from trying to stay relevant while the rules, the portals and the buyer agreements keep changing.'
}] as const;
export const pillars = [{
  icon: 'Rocket',
  title: 'Infinite Lead Pipeline',
  description: 'Generate, qualify and nurture leads on autopilot with tested scripts, sequences and automations that book appointments while you sleep.'
}, {
  icon: 'Landmark',
  title: 'Commission Maximizer',
  description: 'Negotiate like an elite listing agent, dismantle objections on the spot, and defend your fee in every post-settlement conversation.'
}] as const;
export type Volume = {
  n: string;
  title: string;
  pages: string;
  description: string;
  color: string;
  icon: string;
  image: string;
};
export const volumes: Volume[] = [{
  n: '01',
  title: 'The AI Agent Blueprint',
  pages: '90+ pages',
  color: '#D4AF37',
  icon: 'Compass',
  image: 'https://cdn.magicpatterns.com/uploads/xu9G732fatxNEUpqP1NP5q/vol-1-1x1.jpg',
  description: 'Build your "Digital Twin" and automate the 80% of administrative grunt work that drains your energy. Learn to train AI on your specific market data to handle research, planning, and operations with 24/7 precision.'
}, {
  n: '02',
  title: 'The Viral Listing Engine',
  pages: "80+ PAGES",
  color: '#9FC0D8',
  icon: 'Magnet',
  image: 'https://cdn.magicpatterns.com/uploads/jzW8xVYa8tzxXP8g8YqZnr/vol-2-1x1.jpg',
  description: 'Turn every property into a lead-generating magnet using AI-engineered hooks for Reels, TikTok, and property descriptions. Stop the scroll and trigger high-intent inquiries with content designed to exploit social media algorithms.'
}, {
  n: '03',
  title: 'The Ghost Lead Recovery Protocol',
  pages: "50+ PAGES",
  color: '#D4AF37',
  icon: 'Megaphone',
  image: 'https://cdn.magicpatterns.com/uploads/mKUYPVtFSQHM1xXHteDrnE/vol-3-1x1.jpg',
  description: 'The science of reviving "dead" prospects who stopped responding months ago. Deploy AI-powered SMS and email sequences that bypass filters and trigger an immediate "hand-up" response from your cold database.'
}, {
  n: '04',
  title: 'The NAR-Proof Negotiation Vault',
  pages: "50+ PAGES",
  color: '#9FC0D8',
  icon: 'Handshake',
  image: 'https://cdn.magicpatterns.com/uploads/bKFuZpWQ21dm2cVZSWzcme/vol-4-1x1.jpg',
  description: 'Master the new legal landscape with elite scripts designed to justify your commission without hesitation. Use AI-backed frameworks to handle modern objections and win listings in a high-interest, post-settlement market.'
}, {
  n: '05',
  title: 'The Referral & Review Machine',
  pages: "50+ PAGES",
  color: '#D4AF37',
  icon: 'Workflow',
  image: 'https://cdn.magicpatterns.com/uploads/9J8fF5sWGKUU4h8Y2TG4zs/vol-5-1x1.jpg',
  description: 'Transform every closed deal into a permanent source of new business. Automate your post-close touchpoints and review requests so your past clients become a self-sustaining referral engine that grows your wealth on autopilot.'
}];
export type Bonus = {
  title: string;
  description: string;
  value: string;
  color: string;
  image: string;
};
export const bonuses: Bonus[] = [{
  title: "365-Day Content Calendar (Part A and B)",
  image: 'https://cdn.magicpatterns.com/uploads/sG7apdX2u4cbe5q9Gjhoi6/BONO-1-parte-A.png',
  description: "300+ pages of daily, market-ready posts, captions and hooks covering every days of the year. Never stare at a blank feed again.",
  value: "$69",
  color: '#D4AF37'
}, {
  title: '1,000+ Prompt Library',
  image: 'https://cdn.magicpatterns.com/uploads/azUAsjSBZEBzBWYjDkpFAk/BONO-2-the-ultimate-1-000-real-estate-master-prompt-bank.png',
  description: 'Copy-paste prompts for every task in the transaction, organized by stage and role.',
  value: "$21",
  color: '#9FC0D8'
}, {
  title: 'SMS & WhatsApp Scripts',
  image: 'https://cdn.magicpatterns.com/uploads/4KoB9gtkxMtxVtGUL1cKMR/BONO-3-whatsapp-sms-magic-scripts-the-conversion-vault.png',
  description: 'Short-form sequences that get replies — compliant, human, and tested across four countries.',
  value: '$17',
  color: '#D4AF37'
}, {
  title: 'Listing Presentation Overhaul',
  image: 'https://cdn.magicpatterns.com/uploads/gnnjF4Amk1pXcJuj7y2ntB/BONO-4-the-listing-presentation-ai-overhaul.png',
  description: 'An AI-built listing appointment that pre-frames your fee before you walk in the door.',
  value: "$19",
  color: '#9FC0D8'
}, {
  title: 'FSBO & Expired Dominator',
  image: 'https://cdn.magicpatterns.com/uploads/8P8bNi7eYEfmqyWP7BHL1a/BONO-5-volume-5-the-referral-review-machine-post-close-automation.png',
  description: 'The outreach system for the two lead sources nobody else has the stomach to work.',
  value: "$21",
  color: '#D4AF37'
}];
export type Transformation = {
  label: string;
  before: string[];
  after: string[];
};
export const transformations: Transformation[] = [{
  label: 'Lead Flow',
  before: ['Losing leads to silence', 'Chasing cold inquiries manually', 'Feast-or-famine pipeline'],
  after: ['Leads calling YOU for appointments', 'Every inquiry answered in 60 seconds', 'A pipeline that refills itself']
}, {
  label: 'Your Week',
  before: ['30+ hours on admin work', 'Nights spent writing listing copy', 'Always reactive, never ahead'],
  after: ['20 hours per week automated', 'Marketing assets in minutes', 'Time back for showings and closings']
}, {
  label: 'Your Fee',
  before: ['Competing on price, not value', 'Discounting to win the listing', 'Fee questioned at every table'],
  after: ['Commanding premium commissions', 'Winning on demonstrated systems', 'Fee defended before it is raised']
}];
export const testimonials = [{
  name: 'Camila Ortega',
  location: 'Miami, FL',
  title: 'Top Producer, Miami Real Estate',
  quote: 'I rebuilt my follow-up in one weekend. Response rates tripled and I closed two listings I had already written off.',
  avatar: 'https://cdn.magicpatterns.com/patterns/generated-images/affd4647-dc2a-478a-ac42-3361422d2ae2.jpg'
}, {
  name: 'Marcus Bell',
  location: 'Toronto, ON',
  title: 'Broker/Owner, 9-Agent Team',
  quote: 'My whole team runs on Volume 2 now. We answer every lead in under a minute — that alone paid for it a hundred times over.',
  avatar: 'https://cdn.magicpatterns.com/patterns/generated-images/d1c3b44e-4a13-42a5-9c22-6c7f88c37087.jpg'
}, {
  name: 'Hannah Whitfield',
  location: 'Manchester, UK',
  title: 'Lettings & Sales Negotiator',
  quote: 'The listing presentation overhaul is worth the whole system. I stopped discounting my fee and nobody has pushed back since.',
  avatar: 'https://cdn.magicpatterns.com/patterns/generated-images/e30ccf5c-1e11-4ad8-87d2-574693f90b23.jpg'
}, {
  name: 'Daniel Kwan',
  location: 'Sydney, AU',
  title: 'Principal Agent, Prestige Homes',
  quote: 'Twenty years in the business and this is the first system I actually implemented. I got about a day back every week.',
  avatar: 'https://cdn.magicpatterns.com/patterns/generated-images/e4481f61-8444-4bc9-9796-ecc3385c52d5.jpg'
}];
export const stats = [{
  value: '2,000+',
  label: 'Agents using this system'
}, {
  value: '3.2x',
  label: 'Average increase in lead responses'
}, {
  value: '12',
  label: 'Countries in active use'
}, {
  value: '98%',
  label: 'Satisfaction rate'
}];
export const faqs = [{
  q: 'Is this system only for the US market?',
  a: 'No. While it follows the high-performance standards of the US market, the core technology — AI prompts, follow-up sequences, and content frameworks — is universal. Real estate is a human-to-human business powered by speed. Whether you are in London, Lagos, Sydney, or Toronto, this system gives you the unfair advantage needed to outpace your local competition.'
}, {
  q: 'Is this only for experienced agents or beginners too?',
  a: 'Both. New agents use it to skip the first three years of trial and error with ready-made scripts and workflows. Veterans use it to automate the parts of the business they already know cold. Volume 1 tells you exactly where to start based on your production level.'
}, {
  q: 'How much technical knowledge do I need?',
  a: 'If you can send an email, you can run this. Every prompt is copy-and-paste, every automation is documented step by step with screenshots. No coding, no developers, no expensive software stack required.'
}, {
  q: 'Can I use this immediately or is there a setup time?',
  a: 'You can use the prompt library and the 365-day content calendar the minute you log in. Volume 1 is sequenced so you can have your digital brain and your follow-up engine running over a single weekend.'
}, {
  q: "What if I don't have time to implement everything?",
  a: 'You are not meant to. The system is sequenced by return on effort, so the highest-impact pieces come first. Agents who only implement Volume 2 typically see the biggest single jump in response rates.'
}, {
  q: 'Is this legal post-NAR settlement?',
  a: 'Yes. The negotiation and buyer-agreement material was written after the settlement took effect and reflects the current compensation and disclosure landscape. Scripts are designed to be transparent, compliant and brokerage-friendly — always confirm with your broker for local rules.'
}, {
  q: 'Do you provide ongoing support or updates?',
  a: 'Every update to the system is delivered to your library free, for life. AI tools change fast, so material is revised as the platforms and the regulations shift. Support is available by email.'
}, {
  q: "What's your refund policy?",
  a: 'Seven days, no questions asked. Open the volumes, put the scripts and the content calendar to work, and if it has not earned its keep, email us within 7 days and we refund you in full. You keep the bonuses.'
}, {
  q: 'Can I use this in my country (not US)?',
  a: 'Yes — it is in active use across the US, UK, Canada and Australia. The AI frameworks, scripts and automations are market-agnostic; only local compliance language needs adapting, and the system flags where to do that.'
}];